import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Flame, Heart, Send, Loader2, CheckCircle, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

// Helper to build full image URL from relative or absolute path
function getImageUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
}

export default function CondolenceForm({ inseratId, inseratName, onSuccess, onClose, condolenceUrl }) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [tributeImages, setTributeImages] = useState([]);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);

  // Form state
  const [form, setForm] = useState({
    user_name: '',
    user_email: '',
    title: '',
    text: '',
    tribute_image_id: null,
    is_candle_only: false,
    agb_accepted: false,
  });

  // Load tribute images
  useEffect(() => {
    fetch(`${API}/tribute-images`)
      .then(r => r.json())
      .then(data => setTributeImages(data.items || []))
      .catch(console.error);
  }, []);

  // Initialize Turnstile
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current) return;

    // Load Turnstile script if not already loaded
    if (!window.turnstile) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      
      script.onload = () => {
        renderTurnstile();
      };
    } else {
      renderTurnstile();
    }

    function renderTurnstile() {
      if (window.turnstile && turnstileRef.current) {
        window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          'error-callback': () => setError('Captcha-Fehler'),
          theme: 'light',
        });
      }
    }

    return () => {
      if (window.turnstile && turnstileRef.current) {
        window.turnstile.remove(turnstileRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.user_name.trim()) {
      setError('Bitte geben Sie Ihren Namen ein');
      return;
    }
    
    if (!form.agb_accepted) {
      setError('Bitte akzeptieren Sie die AGB');
      return;
    }

    // Skip turnstile check if not configured
    const token = turnstileToken || 'skip';
    
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(condolenceUrl || `${API}/inserate/${inseratId}/condolence`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_name: form.user_name,
          user_email: form.user_email || null,
          title: form.title || null,
          text: form.text || null,
          tribute_image_id: form.tribute_image_id,
          is_candle_only: form.is_candle_only,
          marketing_optin: false,
          turnstile_token: token,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Fehler beim Senden');
      }

      setSuccess(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Candle-only quick action
  const handleCandleOnly = () => {
    setForm(prev => ({
      ...prev,
      is_candle_only: true,
      tribute_image_id: tributeImages.find(i => i.category === 'kerze')?.id || tributeImages[0]?.id,
    }));
  };

  if (success) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="w-16 h-16 text-[#059669] mx-auto mb-4" />
        <h3 className="font-heading text-xl text-[#1E293B] mb-2">Vielen Dank</h3>
        <p className="text-[#475569] mb-6">
          Ihre Kondolenz wurde eingereicht und wird nach Prüfung veröffentlicht.
        </p>
        <Button onClick={onClose} variant="outline" className="rounded-sm">
          Schliessen
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Quick Actions */}
      <div className="flex gap-3 mb-6">
        <Button
          type="button"
          variant={form.is_candle_only ? 'default' : 'outline'}
          onClick={handleCandleOnly}
          className={`flex-1 rounded-sm ${form.is_candle_only ? 'bg-[#C5A059] hover:bg-[#B08D45]' : ''}`}
        >
          <Flame className="w-4 h-4 mr-2" />
          Nur Kerze anzünden
        </Button>
        <Button
          type="button"
          variant={!form.is_candle_only ? 'default' : 'outline'}
          onClick={() => setForm(prev => ({ ...prev, is_candle_only: false }))}
          className={`flex-1 rounded-sm ${!form.is_candle_only ? 'bg-[#C5A059] hover:bg-[#B08D45]' : ''}`}
        >
          <Heart className="w-4 h-4 mr-2" />
          Mit Nachricht
        </Button>
      </div>

      {/* Name (required) */}
      <div>
        <Label className="text-sm text-[#475569]">Ihr Name *</Label>
        <Input
          value={form.user_name}
          onChange={(e) => setForm(prev => ({ ...prev, user_name: e.target.value }))}
          placeholder="Ihr vollständiger Name"
          className="mt-1 rounded-sm"
          data-testid="condolence-name"
        />
      </div>

      {/* Email (optional, internal) */}
      <div>
        <Label className="text-sm text-[#475569]">E-Mail (optional, nicht öffentlich)</Label>
        <Input
          type="email"
          value={form.user_email}
          onChange={(e) => setForm(prev => ({ ...prev, user_email: e.target.value }))}
          placeholder="ihre@email.ch"
          className="mt-1 rounded-sm"
          data-testid="condolence-email"
        />
        <p className="text-xs text-[#94A3B8] mt-1">Wird nicht veröffentlicht</p>
      </div>

      {/* Tribute Image Selection */}
      <div>
        <Label className="text-sm text-[#475569] mb-2 block">Gedenk-Symbol wählen</Label>
        <div className="grid grid-cols-4 gap-3">
          {tributeImages.map(img => (
            <button
              key={img.id}
              type="button"
              onClick={() => setForm(prev => ({ ...prev, tribute_image_id: img.id }))}
              className={`p-2 rounded-sm border-2 transition-all ${
                form.tribute_image_id === img.id
                  ? 'border-[#C5A059] bg-[#FDF8F0]'
                  : 'border-[#E2E8F0] hover:border-[#C5A059]/50'
              }`}
              data-testid={`tribute-${img.id}`}
            >
              <img src={getImageUrl(img.image_url)} alt={img.name} loading="lazy" className="w-12 h-12 mx-auto object-contain" />
              <p className="text-[10px] text-[#475569] mt-1 text-center truncate">{img.name}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Message fields (only if not candle-only) */}
      {!form.is_candle_only && (
        <>
          <div>
            <Label className="text-sm text-[#475569]">Titel (optional)</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="z.B. In stiller Anteilnahme"
              className="mt-1 rounded-sm"
              maxLength={200}
              data-testid="condolence-title"
            />
          </div>

          <div>
            <Label className="text-sm text-[#475569]">Ihre Nachricht</Label>
            <Textarea
              value={form.text}
              onChange={(e) => setForm(prev => ({ ...prev, text: e.target.value }))}
              placeholder="Teilen Sie Ihre Erinnerungen oder Beileidsbekundungen..."
              className="mt-1 rounded-sm"
              rows={4}
              data-testid="condolence-text"
            />
          </div>
        </>
      )}

      {/* Turnstile Captcha */}
      {TURNSTILE_SITE_KEY && (
        <div ref={turnstileRef} className="flex justify-center" data-testid="turnstile-widget" />
      )}

      {/* AGB Checkbox */}
      <div className="flex items-start gap-2">
        <Checkbox
          id="agb"
          checked={form.agb_accepted}
          onCheckedChange={(checked) => setForm(prev => ({ ...prev, agb_accepted: checked }))}
          className="mt-1"
          data-testid="agb-checkbox"
        />
        <label htmlFor="agb" className="text-sm text-[#475569] cursor-pointer">
          Ich akzeptiere die <a href="/seite/nutzungsbedingungen" target="_blank" className="text-[#C5A059] underline">AGB</a> und bestätige, dass mein Beitrag den Regeln des würdevollen Umgangs entspricht.
        </label>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-sm p-3 text-sm text-[#DC2626]">
          {error}
        </div>
      )}

      {/* Submit */}
      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-[#C5A059] hover:bg-[#B08D45] text-white py-3 rounded-sm"
        data-testid="submit-condolence"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Wird gesendet...
          </>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            {form.is_candle_only ? 'Kerze anzünden' : 'Kondolenz senden'}
          </>
        )}
      </Button>
    </form>
  );
}

// Wrapper component with Dialog
export function CondolenceDialog({ open, onOpenChange, inseratId, inseratName, onSuccess, condolenceUrl }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="condolence-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#C5A059]" />
            Kondolenz für {inseratName}
          </DialogTitle>
        </DialogHeader>
        <CondolenceForm
          inseratId={inseratId}
          inseratName={inseratName}
          onSuccess={onSuccess}
          onClose={() => onOpenChange(false)}
          condolenceUrl={condolenceUrl}
        />
      </DialogContent>
    </Dialog>
  );
}
